import { BrandLoader } from '@syncanix/ui';

import { useT } from '@/core/providers/I18nProvider';

import type { ReactElement } from 'react';

export interface AppLoadingProps {
  /** Announced + visible label. Defaults to a branded loading message. */
  label?: string;
}

/**
 * Full-viewport branded loading state (Phase 9 Pass 7 — AF-474).
 *
 * Replaces the blank `<div role="status" />` fallbacks that produced a
 * white flash with nothing announced on cold load. Renders the animated
 * Syncanix brand mark (`BrandLoader`) + a visible, polite-announced label on
 * the themed background, so the wait reads as "the app is booting", not "the
 * page is broken". The mark honours `prefers-reduced-motion` (freezes to a
 * static logo) via `@syncanix/ui`.
 *
 * Used by `AuthGuard` (session hydration) and `RootRedirect` (front-door
 * auth resolution) so both entry paths share one consistent loading look.
 */
export function AppLoading({ label }: AppLoadingProps): ReactElement {
  const { t } = useT();
  const resolvedLabel = label ?? t('common.loadingBranded');
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background text-foreground"
    >
      <BrandLoader variant="spin" size={48} />
      <span className="text-sm text-muted-foreground">{resolvedLabel}</span>
    </div>
  );
}
